/**
 * Research — technology tree. Techs cost resources upfront and complete
 * after their data `research_time`. Completed techs feed TechEffects, which
 * apply data `effects` (gather_rate, unit_stat, train_cost, income,
 * upkeep_cost, carry_capacity, haul_speed) to the systems they touch.
 * Effects the prototype does not simulate yet are ignored gracefully.
 */
import type { GameDB } from "../data/gamedb";
import type { TechDef, TechEffect } from "../data/types";
import type { Economy } from "./economy";

export interface ResearchState {
  completed: Set<string>;
  active: { techId: string; remaining: number; total: number } | null;
}

export function createResearch(): ResearchState {
  return { completed: new Set(), active: null };
}

/** Is a tech researchable right now? Returns the blocking reason if not. */
export function canResearch(
  tech: TechDef,
  state: ResearchState,
  activeBuildingIds: Set<string>,
  echelonIdx: number,
): { ok: boolean; reason: string } {
  if (state.completed.has(tech.id)) return { ok: false, reason: "researched" };
  if (state.active) return { ok: false, reason: "busy" };
  if (tech.echelon > echelonIdx)
    return { ok: false, reason: `requires Echelon ${tech.echelon}` };
  const missingTech = tech.requires.techs.find((t) => !state.completed.has(t));
  if (missingTech) return { ok: false, reason: `requires ${missingTech}` };
  const missingB = tech.requires.buildings.find((b) => !activeBuildingIds.has(b));
  if (missingB) return { ok: false, reason: `requires ${missingB}` };
  if (
    tech.researched_at.length > 0 &&
    !tech.researched_at.some((b) => activeBuildingIds.has(b))
  ) {
    return { ok: false, reason: `research at ${tech.researched_at[0]}` };
  }
  return { ok: true, reason: "" };
}

export function startResearch(
  state: ResearchState,
  tech: TechDef,
  economy: Economy,
): boolean {
  if (!economy.pay(tech.cost)) return false;
  state.active = {
    techId: tech.id,
    remaining: tech.research_time,
    total: tech.research_time,
  };
  return true;
}

/** Advance research; returns the completed tech, if any. */
export function tickResearch(
  state: ResearchState,
  dt: number,
  db: GameDB,
): TechDef | null {
  if (!state.active) return null;
  state.active.remaining -= dt;
  if (state.active.remaining > 0) return null;
  const tech = db.technologies.get(state.active.techId) ?? null;
  state.active = null;
  if (tech) state.completed.add(tech.id);
  return tech;
}

function str(e: TechEffect, key: string): string {
  const v = e[key];
  return typeof v === "string" ? v : "";
}

function num(e: TechEffect, key: string, fb: number): number {
  const v = e[key];
  return typeof v === "number" ? v : fb;
}

/** Aggregates the effects of all completed techs for the live systems. */
export class TechEffects {
  constructor(
    private state: ResearchState,
    private db: GameDB,
  ) {}

  private *completedEffects(): Generator<TechEffect> {
    for (const id of this.state.completed) {
      const tech = this.db.technologies.get(id);
      if (tech) yield* tech.effects;
    }
  }

  /** Multiplicative production bonus for a resource (gather_rate). */
  gatherMultiplier(resource: string): number {
    let m = 1;
    for (const e of this.completedEffects()) {
      if (e.type === "gather_rate" && str(e, "resource") === resource) {
        m *= num(e, "multiplier", 1);
      }
    }
    return m;
  }

  /** Flat income add per second for a resource (income effects). */
  incomeAdd(resource: string): number {
    let add = 0;
    for (const e of this.completedEffects()) {
      if (e.type === "income" && str(e, "resource") === resource) {
        add += num(e, "add", 0);
      }
    }
    return add;
  }

  /** Additive combat stat bonus for a unit role (unit_stat effects). */
  unitStatAdd(role: string | undefined, stat: string): number {
    let add = 0;
    for (const e of this.completedEffects()) {
      if (e.type !== "unit_stat" || str(e, "stat") !== stat) continue;
      const roles = e["roles"];
      if (Array.isArray(roles) && role && (roles as string[]).includes(role)) {
        add += num(e, "add", 0);
      }
    }
    return add;
  }

  /** Training cost multiplier for a unit role (train_cost effects). */
  trainCostMultiplier(role: string | undefined): number {
    let m = 1;
    for (const e of this.completedEffects()) {
      if (e.type !== "train_cost") continue;
      const roles = e["roles"];
      if (Array.isArray(roles) && role && (roles as string[]).includes(role)) {
        m *= num(e, "multiplier", 1);
      }
    }
    return m;
  }

  /** Upkeep multiplier across all units (upkeep_cost effects). */
  upkeepMultiplier(): number {
    let m = 1;
    for (const e of this.completedEffects()) {
      if (e.type === "upkeep_cost") m *= num(e, "multiplier", 1);
    }
    return m;
  }

  /**
   * Command multiplier from completed techs (command_capacity effects).
   * Additive by design: each tier adds to the base, so Dilectus I/II/III
   * give ×1.5 / ×2.0 / ×2.5. Applies to the formation pool (see
   * sim/command.ts) — never to headcount.
   */
  commandMultiplier(): number {
    let m = 1;
    for (const e of this.completedEffects()) {
      if (e.type === "command_capacity") m += num(e, "add", 0);
    }
    return m;
  }

  carryAdd(): number {
    let add = 0;
    for (const e of this.completedEffects()) {
      if (e.type === "carry_capacity") add += num(e, "add", 0);
    }
    return add;
  }

  haulMultiplier(): number {
    let m = 1;
    for (const e of this.completedEffects()) {
      if (e.type === "haul_speed") m *= num(e, "multiplier", 1);
    }
    return m;
  }
}

/** Human-readable one-liner for a tech's effects (UI). */
export function effectSummary(tech: TechDef): string {
  const parts: string[] = [];
  for (const e of tech.effects) {
    switch (e.type) {
      case "gather_rate":
        parts.push(`+${Math.round((num(e, "multiplier", 1) - 1) * 100)}% ${str(e, "resource")} gathering`);
        break;
      case "unit_stat":
        parts.push(`+${num(e, "add", 0)} ${str(e, "stat")} (${(e["roles"] as string[] ?? []).join("/")})`);
        break;
      case "train_cost":
        parts.push(`${Math.round(num(e, "multiplier", 1) * 100)}% train cost`);
        break;
      case "income":
        parts.push(`+${num(e, "add", 0)}/s ${str(e, "resource")} income`);
        break;
      case "upkeep_cost":
        parts.push(`${Math.round(num(e, "multiplier", 1) * 100)}% upkeep`);
        break;
      case "command_capacity":
        parts.push(`+${Math.round(num(e, "add", 0) * 100)}% military command`);
        break;
      case "carry_capacity":
        parts.push(`+${num(e, "add", 0)} carry capacity`);
        break;
      case "haul_speed":
        parts.push(`+${Math.round((num(e, "multiplier", 1) - 1) * 100)}% haul speed`);
        break;
      case "unlock_unit":
        parts.push(`unlocks ${(e["units"] as string[] ?? []).join(", ")}`);
        break;
      case "unlock_building":
        parts.push(`unlocks ${(e["buildings"] as string[] ?? []).join(", ")}`);
        break;
      default:
        parts.push(e.type.replace(/_/g, " "));
        break;
    }
  }
  return parts.join("; ") || "no prototype effect";
}
